export type TypeRunWithConcurrencyOptions<T> = {
    shouldStop?: () => boolean;
    stopOnError?: boolean;
    throwOnError?: boolean;
    onError?: (err: unknown, item: T, index: number) => void;
};

export type TypeRunWithConcurrencyResult<T> = {
    okCount: number;
    errorCount: number;
    errors: { index: number; item: T; error: unknown }[];
};

// ----------------------------------------------------------------------------

async function runWithConcurrency<T>(
    items: T[],
    concurrency: number,
    worker: (item: T, index: number) => Promise<void>,
    options?: TypeRunWithConcurrencyOptions<T>,
): Promise<TypeRunWithConcurrencyResult<T>> {
    /*
     * A non-numeric concurrency would leave `limit` as NaN, which `Array.from` reads as zero
     * runners: every item would be silently left undone and the run would resolve as if empty.
     */
    const requested = Math.floor(Number(concurrency));
    const safeConcurrency = Number.isFinite(requested) ? Math.max(1, requested) : 1;
    const limit = Math.min(safeConcurrency, items.length);

    const shouldStop = options?.shouldStop;
    const stopOnError = options?.stopOnError === true;
    const throwOnError = options?.throwOnError === true;

    let nextIndex = 0;
    let stop = false;

    let okCount = 0;

    const errors: TypeRunWithConcurrencyResult<T>['errors'] = [];

    const runners = Array.from({ length: limit }, async (): Promise<any> => {
        while (true) {
            if (stop) {
                return;
            }

            if (shouldStop?.()) {
                stop = true;
                return;
            }

            const currentIndex = nextIndex;
            nextIndex += 1;

            if (currentIndex >= items.length) {
                return;
            }

            const item = items[currentIndex];

            // One more check after reserving the index: helps stop sooner.
            if (stop) {
                return;
            }

            if (shouldStop?.()) {
                stop = true;
                return;
            }

            try {
                await worker(item, currentIndex);
                okCount += 1;
            } catch (err) {
                errors.push({ index: currentIndex, item, error: err });

                try {
                    options?.onError?.(err, item, currentIndex);
                } catch (handlerErr) {
                    errors.push({ index: currentIndex, item, error: handlerErr });
                }

                if (stopOnError) {
                    stop = true;
                    return;
                }
            }
        }
    });

    await Promise.all(runners);

    const result: TypeRunWithConcurrencyResult<T> = {
        okCount,
        errorCount: errors.length,
        errors,
    };

    if (throwOnError && result.errorCount > 0) {
        const err = result.errors[0]?.error;
        throw err instanceof Error ? err : new Error('Unknown error');
    }

    return result;
}

// ----------------------------------------------------------------------------

export default runWithConcurrency;
